if (!customElements.get('collection-filters')) {
  customElements.define(
    'collection-filters',
    class CollectionFilters extends HTMLElement {
      constructor() {
        super();

        this.cache = {};

        this.currentFilter = '';

        this.productList = document.getElementById('product-grid');

        if (!this.productList) return;

        this.tabs = this.querySelectorAll('[role="tab"]');
        this.panels = this.querySelectorAll('[role="tabpanel"]');

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.tabHandler.bind(this));
        });

        const onHistoryChange = (event) => {
          const searchFilters = event.state && event.state.filter ? event.state.filter : [];

          this.renderFilterView(searchFilters);
          this.setFilter(searchFilters);
        };

        window.addEventListener('popstate', onHistoryChange);

        // clear-selection button click handler
        //const clearSelectionButton = this.querySelector('.clear-selection');
        //if (clearSelectionButton) {
        //  clearSelectionButton.addEventListener('click', () => {
        //    this.resetTabs();
        //    this.resetPanels();
        //  });
        //}

        // The URL can carry more than one filter.p.tag now (one per selected
        // level in the chain), so read them all and reflect the current state.
        const initialFilters = this.getFiltersFromUrl();

        this.currentFilter = this.cacheKey(initialFilters);

        if (initialFilters.length) {
          this.renderFilterView(initialFilters);
        }
      }

      // Read every filter.p.tag value straight from the query string, keeping the
      // encoded form (so it matches each tab's data-encode) and preserving order.
      getFiltersFromUrl() {
        const query = window.location.search.replace(/^\?/, '');

        if (!query) return [];

        return query
          .split('&')
          .filter((pair) => pair.split('=')[0] === 'filter.p.tag')
          .map((pair) => pair.split('=').slice(1).join('='))
          .filter(Boolean);
      }

      // An "All" tab stands for the absence of a value in its own row: selecting
      // it keeps the ancestor filters and leaves that row unconstrained.
      isAllTab(tab) {
        return tab.dataset.isAll === 'true';
      }

      // A tab contributes a value to the filter chain when it sits below the
      // parent row, or when it is a parent that carries no children and is
      // therefore a filter in its own right. Parents that only exist to drill
      // down are navigation, and "All" tabs contribute nothing by design.
      contributesValue(tab) {
        if (this.isAllTab(tab)) return false;

        return parseInt(tab.dataset.level) >= 1 || tab.dataset.isFilter === 'true';
      }

      // Build the ordered chain of encoded filter values that lead to a tab,
      // walking up through the parent tabs.
      getFilterChain(tab) {
        const chain = [];
        let current = tab;

        while (current) {
          if (this.contributesValue(current)) {
            chain.unshift(current.dataset.encode);
          }

          const panel = current.closest('[role="tabpanel"]');

          if (!panel) break;

          const parentTabId = panel.getAttribute('aria-labelledby');
          current = parentTabId ? this.querySelector(`#${parentTabId}`) : null;
        }

        return chain;
      }

      cacheKey(filters) {
        return (filters || []).join('&');
      }

      renderFilterView(filters) {
        this.resetTabs();
        this.resetPanels();

        const tags = filters || [];

        if (!tags.length) return;

        // Multiple tabs can share the same value (e.g. "16x20" under different
        // parents), so match on the full chain rather than a single key. Only the
        // tab whose complete ancestor chain matches the URL is revealed.
        let matched = null;

        this.tabs.forEach((tab) => {
          if (!this.contributesValue(tab) && !this.isAllTab(tab)) return;

          const chain = this.getFilterChain(tab);

          if (chain.length !== tags.length) return;
          if (!chain.every((value) => tags.includes(value))) return;

          // A row's parent tab and that row's "All" tab produce the same chain,
          // so prefer the deepest match: it reveals the child row with "All"
          // selected instead of stopping one level short. Ties keep the first
          // tab in document order.
          if (matched && parseInt(matched.dataset.level) >= parseInt(tab.dataset.level)) return;

          matched = tab;
        });

        if (matched) this.revealChain(matched);
      }

      // Select a tab and walk up its ancestors, selecting each one and unhiding
      // the panel it lives in so the whole drilled-down path is shown.
      revealChain(tab) {
        let current = tab;

        while (current) {
          current.setAttribute('aria-selected', true);

          const panel = current.closest('[role="tabpanel"]');

          if (!panel) break;

          panel.hidden = false;

          const parentTabId = panel.getAttribute('aria-labelledby');
          current = parentTabId ? this.querySelector(`#${parentTabId}`) : null;
        }
      }

      resetTabs() {
        this.tabs.forEach((tab) => {
          tab.setAttribute('aria-selected', false);
        });
      }

      resetPanels() {
        this.panels.forEach((panel) => {
          panel.hidden = true;
        });
      }

      tabHandler(e) {
        const tab = e.currentTarget;
        const level = parseInt(tab.dataset.level);

        if (level == 0) {
          if (tab.getAttribute('aria-selected') === 'true') {
            this.resetTabs();
            this.resetPanels();

            this.setFilter([]);

            return;
          }
        } else if (tab.getAttribute('aria-selected') === 'true') {
          return;
        }

        this.tabs.forEach((tab) => {
          if (parseInt(tab.dataset.level) >= level) {
            tab.setAttribute('aria-selected', false);
          }
        });

        this.panels.forEach((panel) => {
          if (parseInt(panel.dataset.level) > level) {
            panel.hidden = true;
          }
        });

        tab.setAttribute('aria-selected', true);

        if (tab.dataset.isFilter === 'true') {
          // Apply every value in the chain, e.g. "1 inch" + "12x12".
          this.setFilter(this.getFilterChain(tab));
        } else {
          const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
          panel.hidden = false;

          // Choose the first option in the tabpanel.
          // This will cause it to drill down if there are more levels.
          const filter = panel.querySelector('[role="tab"]');
          filter.click();
        }
      }

      setFilter(filters) {
        const tags = Array.isArray(filters) ? filters : filters ? [filters] : [];
        const key = this.cacheKey(tags);

        if (this.currentFilter === key) return;

        this.currentFilter = key;

        if (this.cache[key]) {
          this.renderSectionFromCache(key, tags);
        } else {
          const query = tags.map((tag) => `filter.p.tag=${tag}`).join('&');
          const url = `${window.location.pathname}?section_id=${this.productList.dataset.id}${query ? `&${query}` : ''}`;
          this.renderSectionFromFetch(url, key, tags);
        }
      }

      renderSectionFromFetch(url, key, tags) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = responseText;

            this.cache[key] = html;

            document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

            document
              .getElementById('ProductGridContainer')
              .querySelectorAll('.scroll-trigger')
              .forEach((element) => {
                element.classList.add('scroll-trigger--cancel');
              });

            if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

            this.updateHistory(tags);
          });
      }

      renderSectionFromCache(key, tags) {
        const html = this.cache[key];

        FacetFiltersForm.renderProductGridContainer(html);

        document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

        document
          .getElementById('ProductGridContainer')
          .querySelectorAll('.scroll-trigger')
          .forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

        this.updateHistory(tags);
      }

      updateHistory(tags) {
        if (tags && tags.length) {
          const query = tags.map((tag) => `filter.p.tag=${tag}`).join('&');
          window.history.pushState({ filter: tags }, '', `${window.location.pathname}?${query}`);
        } else {
          window.history.pushState({ filter: [] }, '', window.location.pathname);
        }
      }
    }
  );
}
